/**
 * Single entry point for every call to the backend.
 *
 * The access token is held in memory only - never in localStorage - so a
 * script injected into the page cannot read it back out. The refresh token
 * lives in an httpOnly cookie the browser attaches on its own. When an access
 * token expires mid-session, one refresh is attempted and the original request
 * is replayed; concurrent 401s share that single refresh rather than each
 * firing one of their own.
 */

const BASE = process.env.REACT_APP_API_BASE || "";

let accessToken = null;
let refreshing = null;
const listeners = new Set();
const blockedListeners = new Set();

export function setAccessToken(token) {
  accessToken = token;
}

export function getAccessToken() {
  return accessToken;
}

/** Notified when a refresh fails, so the app can drop back to signed-out. */
export function onAuthLost(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function authLost() {
  accessToken = null;
  listeners.forEach((fn) => fn());
}

/**
 * Notified specifically when the signed-in account turns out to be
 * suspended or banned - distinct from a plain expired session, so the app
 * can show an explanatory modal rather than silently bouncing to the login
 * screen the way an ordinary session timeout does.
 */
export function onAccountBlocked(fn) {
  blockedListeners.add(fn);
  return () => blockedListeners.delete(fn);
}

function accountBlocked(detail) {
  blockedListeners.forEach((fn) => fn(detail));
}

export class ApiError extends Error {
  constructor(status, payload) {
    super(payload?.message || `Request failed (${status})`);
    this.name = "ApiError";
    this.status = status;
    this.code = payload?.error;
    this.detail = payload?.detail;
  }
}

/**
 * Reads a response body as JSON.
 *
 * A body that is not JSON is reported as such rather than being wrapped into
 * an object and handed to a caller expecting real data. That happens for real:
 * serve the built app behind a catch-all that does not proxy /api, and every
 * API call comes back as index.html with a 200 - the page then renders
 * `undefined.map(...)` and takes the whole route down.
 */
async function parse(res) {
  const text = await res.text();
  if (!text) return null;

  const type = res.headers.get("content-type") || "";
  const looksJson = type.includes("json") || /^\s*[[{]/.test(text);

  if (!looksJson) {
    const err = new ApiError(res.ok ? 502 : res.status, {
      error: "bad_gateway",
      message: res.ok
        ? "The API returned a page instead of data. Is the backend running behind /api?"
        : `Request failed (${res.status})`,
    });
    err.nonJson = true;
    throw err;
  }

  try {
    return JSON.parse(text);
  } catch (err) {
    throw new ApiError(502, {
      error: "bad_gateway",
      message: "The API returned a malformed response.",
    });
  }
}

async function refreshOnce() {
  // One in-flight refresh shared by every caller that hit a 401 at the
  // same moment; otherwise a page with four widgets rotates the refresh
  // token four times and three of them lose the race.
  if (!refreshing) {
    refreshing = fetch(`${BASE}/api/auth/refresh`, {
      method: "POST",
      credentials: "include",
    })
      .then(async (res) => {
        // parse() throws on a non-JSON body, which is the right outcome here
        // too: there is no session to restore if the API is not reachable.
        const data = await parse(res);
        if (!res.ok) throw new ApiError(res.status, data);
        accessToken = data.accessToken;
        return data;
      })
      .finally(() => {
        refreshing = null;
      });
  }
  return refreshing;
}

async function request(method, path, body, options = {}) {
  const isForm = body instanceof FormData;
  const headers = { ...(options.headers || {}) };
  if (body !== undefined && !isForm) headers["Content-Type"] = "application/json";
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

  const send = () =>
    fetch(BASE + path, {
      method,
      headers: accessToken
        ? { ...headers, Authorization: `Bearer ${accessToken}` }
        : headers,
      credentials: "include",
      body: body === undefined ? undefined : isForm ? body : JSON.stringify(body),
      signal: options.signal,
    });

  let res = await send();

  // A 401 from the sign-in endpoints is the answer to the question asked -
  // wrong password, or no account - not a session that went stale mid-visit.
  // Retrying it through the refresh path turned every failed login into
  // "Your session expired. Sign in again.", hiding the real reason and
  // signing the visitor out of a session they never had.
  const isSignInAttempt = /^\/api\/auth\/(login|register|refresh)\b/.test(path);

  if (res.status === 401 && !options.noRetry && !isSignInAttempt) {
    try {
      await refreshOnce();
      res = await send();
    } catch (err) {
      authLost();
      throw new ApiError(401, { message: "Your session expired. Sign in again." });
    }
  }

  // parse() raises for a non-JSON body; a JSON error body still carries the
  // server's own code and message, which is what callers show the user.
  const payload = await parse(res);
  if (!res.ok) {
    if (payload?.error === "account_suspended" || payload?.error === "account_banned") {
      authLost();
      accountBlocked({ code: payload.error, message: payload.message });
    }
    throw new ApiError(res.status, payload);
  }
  return payload;
}

export const api = {
  get: (p, o) => request("GET", p, undefined, o),
  post: (p, b, o) => request("POST", p, b, o),
  patch: (p, b, o) => request("PATCH", p, b, o),
  put: (p, b, o) => request("PUT", p, b, o),
  del: (p, b, o) => request("DELETE", p, b, o),
  refresh: refreshOnce,
  base: BASE,
};

export default api;
